import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { Plus, X, ShoppingBag, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import BookingDrawer from '../components/BookingDrawer';

import { todayIST, nowIST, fmtDateIST, shiftDayIST } from '../../utils/ist';

/** Returns whether any booking in the group has rental items. */
function dedupeGroupRentals(group) {
  const hasRentals = group.some(b => b.rentals?.length > 0);
  return { hasRentals };
}

/** Parse a slot_code like "0900-1030" → { start: "09:00", end: "10:30" }. */
function slotRange(slotId) {
  const [a, b] = (slotId || '').split('-');
  const fmt = (t) => (t && t.length >= 4) ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : (t || '');
  return { start: fmt(a), end: fmt(b) };
}

/**
 * Group a session's bookings by court, merging back-to-back hours into ranges.
 * Mirrors the reference mergeSlots (SPEC §3.4). Returns [{ court, ranges, count }].
 */
function mergeSlots(group) {
  const byCourt = {};
  for (const b of group) {
    const court = b.ground_id;
    (byCourt[court] = byCourt[court] || []).push(slotRange(b.slot_id));
  }
  return Object.keys(byCourt).map(court => {
    const sorted = byCourt[court].slice().sort((a, b) => a.start.localeCompare(b.start));
    const ranges = [];
    for (const s of sorted) {
      const last = ranges[ranges.length - 1];
      if (last && last.end === s.start) last.end = s.end;
      else ranges.push({ start: s.start, end: s.end });
    }
    return { court, ranges, count: sorted.length };
  });
}

/** Contiguous = a single court with a single uninterrupted range. */
function isContiguousGroup(group) {
  const m = mergeSlots(group);
  return m.length === 1 && m[0].ranges.length === 1;
}

// Locations loaded dynamically from /api/facilities

function PaymentDialog({ booking, slotTotal, onClose, onSave, api }) {
  const [method, setMethod] = useState(booking.payment?.method || 'Cash');
  const [amount, setAmount] = useState(booking.payment?.amount ?? '');
  const [txId, setTxId]     = useState(booking.payment?.transaction_id || '');
  const [saving, setSaving] = useState(false);

  // Derive status automatically: Paid when collected >= total (if total known),
  // or when collected > 0 and no total configured. Otherwise Pending.
  const collected  = parseFloat(amount) || 0;
  const autoStatus = (() => {
    if (collected <= 0) return 'Pending';
    if (slotTotal > 0) return collected >= slotTotal ? 'Paid' : 'Pending';
    return 'Paid'; // no pricing configured — treat any amount as paid
  })();
  const remaining = slotTotal > 0 ? Math.max(0, slotTotal - collected) : 0;

  const handleSave = async () => {
    if (!amount) { toast.error('Enter an amount'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/bookings/${booking.booking_id}/payment`, {
        method,
        amount: collected,
        status: autoStatus,
        transaction_id: method === 'UPI' ? txId : null,
      });
      toast.success('Payment updated');
      onSave();
      onClose();
    } catch {
      toast.error('Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 460 }}>
        <div className="sb-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="sb-modal-title">Record Payment</div>
            <div className="sb-modal-sub">{booking.customer_name} · {booking.slot_time} · {booking.date}</div>
          </div>
          <button className="sb-btn ghost icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Due summary */}
          {slotTotal > 0 && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--ink-3)' }}>Total</span>
                <span className="sb-mono sb-num" style={{ fontWeight: 500 }}>₹{slotTotal}</span>
              </div>
              {collected > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                  <span style={{ color: 'var(--ink-3)' }}>Still pending</span>
                  <span className="sb-mono sb-num" style={{ color: remaining === 0 ? 'var(--ok-fg)' : 'var(--warn-fg)', fontWeight: 600 }}>
                    {remaining === 0 ? '✓ Cleared' : `₹${remaining}`}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Quick-fill */}
          {slotTotal > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setAmount(String(slotTotal))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--ok-fg)' }}>
                Full ₹{slotTotal}
              </button>
              {slotTotal > 0 && collected > 0 && remaining > 0 && (
                <button type="button" onClick={() => setAmount(String(remaining))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--warn-fg)' }}>
                  Balance ₹{remaining}
                </button>
              )}
            </div>
          )}

          {/* Method */}
          <div className="sb-field">
            <label>Method</label>
            <div className="sb-chipset" style={{ width: '100%', display: 'flex' }}>
              {['Cash', 'UPI'].map(m => (
                <div key={m} className={`sb-chip ${method === m ? 'active' : ''}`}
                  onClick={() => setMethod(m)} style={{ flex: 1, textAlign: 'center' }}>
                  {m}
                </div>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div className="sb-field">
            <label>Amount collected (₹)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" autoFocus min={0} />
          </div>

          {/* Status indicator */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--ink-3)' }}>Status</span>
            <span className={`sb-badge ${autoStatus === 'Paid' ? 'ok' : 'warn'}`}>
              {autoStatus === 'Paid' ? '✓ Paid' : collected > 0 ? `Pending — ₹${remaining} remaining` : 'Pending'}
            </span>
          </div>

          {method === 'UPI' && (
            <div className="sb-field">
              <label>UPI Transaction ID</label>
              <input type="text" value={txId} onChange={e => setTxId(e.target.value)} placeholder="e.g. 3289xxxx" />
            </div>
          )}
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn ghost">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn brand">
            {saving ? 'Saving…' : 'Save payment'}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Payment dialog for a group of bookings (same customer, date, location).
 * Rentals are counted once when they're shared; on save the total is distributed
 * across individual booking records automatically.
 */
function GroupPaymentDialog({ group, calcSlotTotal, onClose, onSave, api }) {
  const b0 = group[0];
  const [method, setMethod] = useState(b0.payment?.method || 'Cash');
  const [amount, setAmount] = useState('');
  const [txId, setTxId] = useState(b0.payment?.transaction_id || '');
  const [saving, setSaving] = useState(false);

  const courtTotal = group.reduce((s, b) => s + calcSlotTotal(b), 0);
  // Always sum each slot's rental cost — never deduplicate across slots.
  const rentalTotal = group.reduce((s, b) => s + (b.rentals?.reduce((s2, r) => s2 + r.total, 0) || 0), 0);
  const groupTotal = courtTotal + rentalTotal;

  const collected = parseFloat(amount) || 0;
  const autoStatus = groupTotal > 0
    ? (collected >= groupTotal ? 'Paid' : 'Pending')
    : (collected > 0 ? 'Paid' : 'Pending');
  const remaining = groupTotal > 0 ? Math.max(0, groupTotal - collected) : 0;
  const prevCollected = group.reduce((s, b) => s + (parseFloat((b.payment || {}).amount) || 0), 0);

  const handleSave = async () => {
    if (!amount) { toast.error('Enter an amount'); return; }
    setSaving(true);
    try {
      if (autoStatus === 'Paid') {
        // Each booking gets its own slot cost + its own rental cost
        await Promise.all(group.map((b) => {
          const bCourt  = calcSlotTotal(b);
          const bRental = b.rentals?.reduce((s, r) => s + r.total, 0) || 0;
          return api.patch(`/api/bookings/${b.booking_id}/payment`, {
            method, amount: bCourt + bRental, status: 'Paid',
            transaction_id: method === 'UPI' ? txId : null,
          });
        }));
      } else {
        // Partial: record entered amount on first booking, mark others as Pending/0
        await Promise.all(group.map((b, i) =>
          api.patch(`/api/bookings/${b.booking_id}/payment`, {
            method, amount: i === 0 ? collected : 0, status: 'Pending',
            transaction_id: method === 'UPI' ? txId : null,
          })
        ));
      }
      toast.success(`Payment recorded for ${group.length} slot${group.length !== 1 ? 's' : ''}`);
      onSave();
      onClose();
    } catch {
      toast.error('Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 480 }}>
        <div className="sb-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="sb-modal-title">Record Payment</div>
            <div className="sb-modal-sub">{b0.customer_name} · {b0.date} · {group.length} slot{group.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="sb-btn ghost icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Breakdown */}
          {groupTotal > 0 && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px' }}>
              {group.map(b => {
                const bc = calcSlotTotal(b);
                return (
                  <div key={b.booking_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                    <span style={{ color: 'var(--ink-3)' }}>{b.ground_id} · {b.slot_time}</span>
                    <span className="sb-mono">{bc > 0 ? `₹${bc}` : '—'}</span>
                  </div>
                );
              })}
              {rentalTotal > 0 && (
                <>
                  <hr className="sb-divider" style={{ margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                    <span style={{ color: 'var(--ink-3)' }}>Rentals</span>
                    <span className="sb-mono">₹{rentalTotal}</span>
                  </div>
                </>
              )}
              <hr className="sb-divider" style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, fontWeight: 600 }}>
                <span>Total due</span>
                <span className="sb-mono sb-num" style={{ color: 'var(--warn-fg)' }}>₹{groupTotal}</span>
              </div>
            </div>
          )}

          {/* Quick-fill */}
          {groupTotal > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setAmount(String(groupTotal))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--ok-fg)' }}>
                Full ₹{groupTotal}
              </button>
              {prevCollected > 0 && remaining > 0 && (
                <button type="button" onClick={() => setAmount(String(remaining))} className="sb-btn ghost sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--warn-fg)' }}>
                  Balance ₹{remaining}
                </button>
              )}
            </div>
          )}

          {/* Method */}
          <div className="sb-field">
            <label>Method</label>
            <div className="sb-chipset" style={{ width: '100%', display: 'flex' }}>
              {['Cash', 'UPI'].map(m => (
                <div key={m} className={`sb-chip ${method === m ? 'active' : ''}`}
                  onClick={() => setMethod(m)} style={{ flex: 1, textAlign: 'center' }}>
                  {m}
                </div>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div className="sb-field">
            <label>Amount collected (₹)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" autoFocus min={0} />
          </div>

          {/* Status */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--ink-3)' }}>Status</span>
            <span className={`sb-badge ${autoStatus === 'Paid' ? 'ok' : 'warn'}`}>
              {autoStatus === 'Paid' ? `✓ All ${group.length} slots paid` : collected > 0 ? `Pending — ₹${remaining} remaining` : 'Pending'}
            </span>
          </div>

          {method === 'UPI' && (
            <div className="sb-field">
              <label>UPI Transaction ID</label>
              <input type="text" value={txId} onChange={e => setTxId(e.target.value)} placeholder="e.g. 3289xxxx" />
            </div>
          )}
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn ghost">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="sb-btn brand">
            {saving ? 'Saving…' : 'Save payment'}
          </button>
        </div>
      </div>
    </>
  );
}

/** Confirmation modal for cancelling a booking or a multi-slot group. */
function CancelDialog({ group, onClose, onConfirm, cancelling }) {
  const b0 = group[0];
  const isGroup = group.length > 1;
  const isRental = b0.booking_type === 'rental' || b0.ground_id === '__rental__';

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 440 }}>
        <div className="sb-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bad-bg)', color: 'var(--bad-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div className="sb-modal-title">Cancel {isGroup ? `${group.length} bookings` : 'booking'}?</div>
              <div className="sb-modal-sub">{b0.customer_name} · {b0.date}</div>
            </div>
          </div>
          <button className="sb-btn ghost icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.5, margin: 0 }}>
            This will free up {isGroup ? 'these slots' : 'this slot'} and mark the booking as cancelled.
            This action can’t be undone.
          </p>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {group.map(b => (
              <div key={b.booking_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ color: 'var(--ink-3)' }}>{isRental ? 'Rental' : b.ground_id} · {b.slot_time}</span>
                <span className="sb-mono" style={{ color: 'var(--ink-4)' }}>#{b.booking_id.slice(-6)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="sb-modal-foot">
          <button onClick={onClose} className="sb-btn ghost" disabled={cancelling}>Keep booking</button>
          <button onClick={onConfirm} disabled={cancelling} className="sb-btn danger">
            {cancelling ? 'Cancelling…' : `Cancel ${isGroup ? 'all' : 'booking'}`}
          </button>
        </div>
      </div>
    </>
  );
}


const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

export default function AdminBookings() {
  const { user, api } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_groups: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [initialLoad, setInitialLoad] = useState(true); // true only on very first fetch
  const [fetching, setFetching] = useState(false);      // true on subsequent fetches
  const today = todayIST();
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ location_id: '', status: '' });
  const [paymentFilter, setPaymentFilter] = useState(''); // '' | 'pending' (client-side)
  const [datePreset, setDatePreset] = useState('day');
  const [dayDate, setDayDate] = useState(today);
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [groupPaymentGroup, setGroupPaymentGroup] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // { group, isGroup } pending cancel
  const [cancelling, setCancelling] = useState(false);
  const [drawerBooking, setDrawerBooking] = useState(null); // booking open in side drawer
  const [drawerGroup, setDrawerGroup] = useState(null);     // full session group for the drawer
  const [drawerTotal, setDrawerTotal] = useState(0);        // group total for the drawer
  const fetchCountRef = useRef(0);
  const searchRef = useRef(null);
  const { facilities } = useFacilities();

  // ⌘K / Ctrl-K focuses the search box
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Apply a preset filter passed via navigation — e.g. the dashboard's
  // "Pending collection" card deep-links here with { date, status, payment }.
  useEffect(() => {
    const st = location.state;
    if (!st) return;
    if (st.payment) setPaymentFilter(st.payment);
    if (st.status) setFilters(f => ({ ...f, status: st.status }));
    if (st.location_id) setFilters(f => ({ ...f, location_id: String(st.location_id) }));
    if (st.date) { setDatePreset('day'); setDayDate(st.date); }
    // Clear so it doesn't re-apply on back/forward or refresh.
    window.history.replaceState({}, document.title);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Slot pricing helpers ───────────────────────────────────────────────────
  // Map each ground's custom slots by (location:ground:slot_code) → current price,
  // used as a fallback for older bookings with no court_price snapshot.
  const slotPriceMap = useMemo(() => {
    const map = new Map();
    for (const facility of facilities) {
      for (const ground of (facility.grounds || [])) {
        for (const slot of (ground.slots || [])) {
          map.set(`${facility.location_id}:${ground.id}:${slot.slot_code}`, slot.price || 0);
        }
      }
    }
    return map;
  }, [facilities]);

  const calcSlotTotal = useCallback((booking) => {
    // Prefer the price snapshotted at booking time; fall back to the current slot price.
    if (booking.court_price != null) return booking.court_price;
    return slotPriceMap.get(`${booking.location_id}:${booking.ground_id}:${booking.slot_id}`) || 0;
  }, [slotPriceMap]);

  const allowedLocations = user?.role === 'owner'
    ? facilities
    : facilities.filter(l => user?.assigned_locations?.includes(l.location_id));

  // Adapt facilities shape ({ location_id, name }) to the { id, name } shape used in dropdowns
  const locationOptions = allowedLocations.map(f => ({ id: f.location_id, name: f.name }));

  const fetchBookings = useCallback(() => {
    const seq = ++fetchCountRef.current;
    setFetching(true);
    const params = { page, page_size: pageSize };
    if (filters.location_id) params.location_id = filters.location_id;
    if (filters.status) params.status = filters.status;

    // Compute date params from the active preset — all arithmetic in IST
    const now = nowIST(); // Date object with local fields = IST wall clock
    const y = now.getFullYear(), mo = now.getMonth();
    const fmt = d => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    switch (datePreset) {
      case 'day':
        params.date = dayDate;
        break;
      case 'week': {
        const d = new Date(now);
        const dow = d.getDay();
        d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1)); // back to Monday (IST)
        const weekEnd = new Date(d);
        weekEnd.setDate(d.getDate() + 6); // through Sunday
        params.date_from = fmt(d);
        params.date_to = fmt(weekEnd);
        break;
      }
      case 'month':
        params.date_from = fmt(new Date(y, mo, 1));
        params.date_to = fmt(new Date(y, mo + 1, 0));
        break;
      case 'lastMonth':
        params.date_from = fmt(new Date(y, mo - 1, 1));
        params.date_to = fmt(new Date(y, mo, 0));
        break;
      case 'quarter': {
        const qStart = Math.floor(mo / 3) * 3;
        params.date_from = fmt(new Date(y, qStart, 1));
        params.date_to = fmt(new Date(y, qStart + 3, 0));
        break;
      }
      case 'ytd':
        params.date_from = fmt(new Date(y, 0, 1));
        params.date_to = fmt(new Date(y, 11, 31));
        break;
      case 'year':
        params.date_from = fmt(new Date(y, 0, 1));
        params.date_to = fmt(new Date(y, 11, 31));
        break;
      case 'all':
        break;
      case 'custom':
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
        break;
      default: break;
    }

    api.get('/api/bookings/', { params })
      .then(res => {
        if (seq !== fetchCountRef.current) return; // stale response
        const { bookings: bList, ...pageInfo } = res.data;
        setBookings(bList);
        setPagination(pageInfo);
      })
      .catch(err => {
        // 401/403 = token expired — AuthContext will redirect to login, no toast needed
        if (err?.response?.status === 401 || err?.response?.status === 403) return;
        toast.error('Failed to load bookings');
      })
      .finally(() => {
        if (seq !== fetchCountRef.current) return;
        setFetching(false);
        setInitialLoad(false);
      });
  }, [api, filters, datePreset, dayDate, customFrom, customTo, today, page, pageSize]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Group bookings by session_id (set when bulk-creating in one submit).
  // Bookings without a session_id are treated as individual singleton groups.
  const groupedBookings = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const b of bookings) {
      // Only group when a real session_id exists; otherwise each booking stands alone.
      const key = b.session_id || `__solo__${b.booking_id}`;
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(b);
    }
    return order.map(k => map.get(k));
  }, [bookings]);

  // Pre-compute all financial totals per group so the render loop is pure display work.
  // This only re-runs when bookings or pricing data changes — not on every UI interaction
  // (confirmCancel, fetching, etc.) that would otherwise recompute everything.
  const processedGroups = useMemo(() => groupedBookings.map(group => {
    const isGroup = group.length > 1;
    const { hasRentals: hasGroupRentals } = dedupeGroupRentals(group);
    const totalRentalQty  = group.reduce((s, b) =>
      s + (b.rentals || []).reduce((rs, r) => rs + (r.quantity || 0), 0), 0);
    const courtTotal      = isGroup ? group.reduce((s, b) => s + calcSlotTotal(b), 0) : 0;
    const rentalTotal     = isGroup
      ? group.reduce((s, b) => s + (b.rentals?.reduce((s2, r) => s2 + r.total, 0) || 0), 0)
      : 0;
    const groupTotal      = courtTotal + rentalTotal;
    const groupCollected  = isGroup
      ? group.reduce((s, b) => s + (parseFloat((b.payment || {}).amount) || 0), 0)
      : 0;
    const groupFullyPaid  = isGroup && groupTotal > 0 && groupCollected >= groupTotal;

    const rows = group.map(b => {
      const slotTotal = calcSlotTotal(b);
      const collected = parseFloat((b.payment || {}).amount) || 0;
      const payStatus = (b.payment || {}).status || 'Pending';
      const method    = (b.payment || {}).method;
      const remaining = slotTotal > 0 ? Math.max(0, slotTotal - collected) : 0;
      const fullyPaid = slotTotal > 0 && remaining === 0 && collected > 0;
      return { b, slotTotal, collected, payStatus, method, remaining, fullyPaid };
    });

    return { group, isGroup, hasGroupRentals, totalRentalQty, courtTotal, rentalTotal,
             groupTotal, groupCollected, groupFullyPaid, rows };
  }), [groupedBookings, calcSlotTotal]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    const { group } = cancelTarget;
    setCancelling(true);
    try {
      await Promise.all(group.map(b => api.patch(`/api/bookings/${b.booking_id}/cancel`)));
      toast.success(group.length > 1 ? `${group.length} bookings cancelled` : 'Booking cancelled');
      setCancelTarget(null);
      fetchBookings();
    } catch {
      toast.error('Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  // Reset to page 1 whenever a filter or date changes
  const resetPage = () => setPage(1);
  const setFiltersAndReset = (fn) => { resetPage(); setFilters(fn); };
  const setPaymentFilterAndReset = (v) => { resetPage(); setPaymentFilter(v); };
  const setDatePresetAndReset = (k) => { resetPage(); setDatePreset(k); };
  const setDayDateAndReset = (d) => { resetPage(); setDayDate(d); };
  const setCustomFromAndReset = (d) => { resetPage(); setCustomFrom(d); };
  const setCustomToAndReset = (d) => { resetPage(); setCustomTo(d); };

  const shiftDay = (n) => setDayDateAndReset(shiftDayIST(dayDate, n));

  const DATE_PRESETS = [
    { key: 'day',       label: 'Day' },
    { key: 'week',      label: 'This Week' },
    { key: 'month',     label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'quarter',   label: 'This Quarter' },
    { key: 'ytd',       label: 'YTD' },
    { key: 'year',      label: 'This Year' },
    { key: 'all',       label: 'All Time' },
    { key: 'custom',    label: 'Custom' },
  ];

  // Human-readable label for the "Day" pill
  const yesterday = shiftDayIST(today, -1);
  const tomorrow  = shiftDayIST(today, +1);
  const dayPillLabel = dayDate === today ? 'Today' : dayDate === yesterday ? 'Yesterday'
    : dayDate === tomorrow ? 'Tomorrow'
    : fmtDateIST(dayDate);

  // Client-side search over the loaded page (name / phone / booking id).
  // SPEC §17: production should call a backend search endpoint.
  const q = query.trim().toLowerCase();
  const matchesQuery = (pg) => {
    if (!q) return true;
    return pg.group.some(b =>
      `${b.customer_name || ''} ${b.customer_phone || ''} ${b.booking_id || ''}`.toLowerCase().includes(q)
    );
  };

  // "Pending collection" = the group has at least one CONFIRMED slot whose payment
  // isn't settled. Cancelled bookings are excluded (nothing to collect on them) — so
  // the filter stays correct even when the status chip is "Any status".
  const isPendingGroup = (pg) => pg.rows.some(r => r.b.status === 'confirmed' && r.payStatus === 'Pending');

  // Group processedGroups by date for the day-grouped display
  const byDate = {};
  const dateOrder = [];
  for (const pg of processedGroups) {
    if (!matchesQuery(pg)) continue;
    if (paymentFilter === 'pending' && !isPendingGroup(pg)) continue;
    const date = pg.group[0].date;
    if (!byDate[date]) { byDate[date] = []; dateOrder.push(date); }
    byDate[date].push(pg);
  }

  // Derive initials + avatar color from customer name
  function customerInitials(name = '') {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  function customerAvc(name = '') {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 5;
    return h;
  }

  return (
    <div>
      {/* Page header */}
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations</div>
          <h1>Bookings</h1>
          <div className="sb-sub">
            All upcoming court reservations and equipment rentals across your facilities.
            {fetching && !initialLoad && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--ink-4)' }}>Refreshing…</span>}
          </div>
        </div>
        <div className="sb-page-actions">
          <Link to="/admin/bookings/rentals/new" className="sb-btn" style={{ textDecoration: 'none' }}>
            <ShoppingBag size={13} /> New rental
          </Link>
          <Link to="/admin/bookings/new" className="sb-btn primary" style={{ textDecoration: 'none' }}>
            <Plus size={13} /> New booking
          </Link>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sb-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
        {/* Search */}
        <div className="sb-search">
          <svg viewBox="0 0 16 16" width={14} height={14} fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5l3 3" strokeLinecap="round" />
          </svg>
          <input
            ref={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, phone or booking #"
          />
          <span className="sb-kbd">⌘ K</span>
        </div>

        {/* Location filter */}
        {locationOptions.length > 1 && (
          <div className="sb-chipset">
            {[{ id: '', name: 'All locations' }, ...locationOptions].map(l => (
              <div
                key={l.id}
                className={`sb-chip ${String(filters.location_id) === String(l.id) ? 'active' : ''}`}
                onClick={() => setFiltersAndReset(f => ({ ...f, location_id: String(l.id) }))}
              >
                {l.name}
              </div>
            ))}
          </div>
        )}

        {/* Status filter */}
        <div className="sb-chipset">
          {[{ value: '', label: 'Any status' }, { value: 'confirmed', label: 'Confirmed' }, { value: 'cancelled', label: 'Cancelled' }].map(s => (
            <div
              key={s.value}
              className={`sb-chip ${filters.status === s.value ? 'active' : ''}`}
              onClick={() => setFiltersAndReset(f => ({ ...f, status: s.value }))}
            >
              {s.label}
            </div>
          ))}
        </div>

        {/* Payment filter */}
        <div className="sb-chipset">
          {[{ value: '', label: 'Any payment' }, { value: 'pending', label: 'Pending collection' }].map(s => (
            <div
              key={s.value}
              className={`sb-chip ${paymentFilter === s.value ? 'active' : ''}`}
              onClick={() => setPaymentFilterAndReset(s.value)}
            >
              {s.label}
            </div>
          ))}
        </div>

        {/* Period filter */}
        <div className="sb-chipset">
          {DATE_PRESETS.map(p => (
            <div
              key={p.key}
              className={`sb-chip ${datePreset === p.key ? 'active' : ''}`}
              onClick={() => setDatePresetAndReset(p.key)}
            >
              {p.key === 'day' ? dayPillLabel : p.label}
            </div>
          ))}
        </div>

        {/* Clear filters */}
        {(filters.location_id || filters.status) && (
          <button
            onClick={() => setFiltersAndReset(() => ({ location_id: '', status: '' }))}
            className="sb-btn ghost sm"
            style={{ color: 'var(--ink-3)' }}
          >
            <X size={11} /> Clear
          </button>
        )}
      </div>

      {/* Day navigator */}
      {datePreset === 'day' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20 }}>
          <button onClick={() => shiftDay(-1)} className="sb-btn ghost icon"><ChevronLeft size={14} /></button>
          <input
            type="date"
            value={dayDate}
            onChange={e => setDayDateAndReset(e.target.value)}
            className="sb-form-input"
            style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
          />
          <button onClick={() => shiftDay(1)} className="sb-btn ghost icon"><ChevronRight size={14} /></button>
          {dayDate !== today && (
            <button onClick={() => setDayDateAndReset(today)} className="sb-btn ghost sm">Today</button>
          )}
        </div>
      )}

      {/* Custom date range */}
      {datePreset === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <input
            type="date" value={customFrom} onChange={e => setCustomFromAndReset(e.target.value)}
            className="sb-form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
          />
          <span style={{ color: 'var(--ink-4)', fontSize: 13 }}>→</span>
          <input
            type="date" value={customTo} onChange={e => setCustomToAndReset(e.target.value)}
            className="sb-form-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
          />
        </div>
      )}

      {/* Bookings list — day-grouped */}
      <div className="sb-bookings" style={{ opacity: fetching && !initialLoad ? 0.7 : 1, transition: 'opacity .15s' }}>
        {/* Column headers */}
        <div className="sb-bk-col-headers">
          <div className="sb-bk-col-header">Time</div>
          <div className="sb-bk-col-header">Customer</div>
          <div className="sb-bk-col-header">Court / Facility</div>
          <div className="sb-bk-col-header">Tags & notes</div>
          <div className="sb-bk-col-header right">Amount</div>
          <div className="sb-bk-col-header" />
        </div>

        {initialLoad ? (
          /* Skeleton */
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sb-booking" style={{ opacity: 0.5, pointerEvents: 'none' }}>
              {Array.from({ length: 5 }).map((__, j) => (
                <div key={j} style={{ height: 14, background: 'var(--line)', borderRadius: 6, width: j === 0 ? 50 : j === 4 ? 60 : '100%' }} />
              ))}
              <div />
            </div>
          ))
        ) : !initialLoad && bookings.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center', color: 'var(--ink-3)', border: '1px dashed var(--line)', borderRadius: 14, background: 'var(--surface)' }}>
            <div style={{ fontSize: 15, marginBottom: 8 }}>No bookings match these filters</div>
            <Link to="/admin/bookings/new" style={{ color: 'var(--brand)', fontSize: 13, textDecoration: 'none' }}>
              Create the first booking →
            </Link>
          </div>
        ) : (
          dateOrder.map(date => {
            const dayGroups = byDate[date];
            const d = new Date(date + 'T12:00:00+05:30');
            const dayStr = d.toLocaleDateString('en-IN', { weekday: 'long', timeZone: 'Asia/Kolkata' });
            const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
            const revenue = dayGroups.reduce((s, pg) => s + (pg.isGroup ? pg.groupTotal : (pg.rows[0]?.slotTotal || 0)), 0);
            const count = dayGroups.length;

            return (
              <div key={date} className="sb-day-group">
                {/* Day header */}
                <div className="sb-day-head">
                  <div>
                    <div className="sb-day-date">{dateStr}</div>
                    <div className="sb-day-label">{dayStr}</div>
                  </div>
                  <div className="sb-day-meta">
                    {count} booking{count !== 1 ? 's' : ''} · <span className="sb-num">₹{revenue.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Booking rows for this day */}
                {dayGroups.map(({ group, isGroup, hasGroupRentals, totalRentalQty, rentalTotal, groupTotal, groupCollected, groupFullyPaid, rows }) => {
                  const b0 = group[0];
                  const r0 = rows[0];
                  const isRental = b0.booking_type === 'rental' || b0.ground_id === '__rental__';
                  const locationName = locationOptions.find(l => l.id === b0.location_id)?.name || `Location ${b0.location_id}`;

                  // Time display
                  const slotStart = b0.slot_time?.split(/[-–]/)[0]?.trim() || '—';
                  const slotEnd = isGroup ? group[group.length - 1].slot_time?.split(/[-–]/)[1]?.trim() : b0.slot_time?.split(/[-–]/)[1]?.trim();

                  // Money display for the group
                  const totalAmt = isGroup ? groupTotal : r0.slotTotal;
                  const collectedAmt = isGroup ? groupCollected : r0.collected;
                  const dueAmt = totalAmt > 0 ? Math.max(0, totalAmt - collectedAmt) : 0;
                  const isPaid = isGroup ? groupFullyPaid : r0.fullyPaid;

                  // First booking's booking_id for edit/cancel
                  const firstBid = b0.booking_id;
                  const b0Status = b0.status;

                  // Court/slot merging (non-rental groups) + non-contiguous detection
                  const merged = (!isRental && isGroup) ? mergeSlots(group) : null;
                  const courtCount = merged ? merged.length : 1;
                  const isMultiTime = !isRental && group.length > 1 && !isContiguousGroup(group);

                  return (
                    <div
                      key={isGroup ? group.map(b => b.booking_id).join('-') : b0.booking_id}
                      className="sb-booking"
                      onClick={() => { setDrawerBooking(b0); setDrawerGroup(group); setDrawerTotal(isGroup ? groupTotal : r0.slotTotal); }}
                    >
                      {/* Time */}
                      <div className="sb-bk-time">
                        {slotStart}
                        {slotEnd && <span className="sb-end">— {slotEnd}</span>}
                        {isGroup && (
                          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--brand)', fontWeight: 600, marginTop: 2 }}>
                            {group.length} sessions
                          </span>
                        )}
                      </div>

                      {/* Customer */}
                      <div className="sb-bk-who">
                        <div
                          className={`sb-av c${customerAvc(b0.customer_name)}`}
                          style={{ width: 32, height: 32, fontSize: 11.5, flexShrink: 0 }}
                        >
                          {customerInitials(b0.customer_name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="sb-bk-name">{b0.customer_name}</div>
                          <div className="sb-bk-phone">{b0.customer_phone}</div>
                        </div>
                      </div>

                      {/* Court / Facility */}
                      <div className="sb-bk-where" style={{ minWidth: 0 }}>
                        {merged ? (
                          <>
                            <div className="sb-bk-court" style={{ marginBottom: 3 }}>
                              {courtCount === 1 ? merged[0].court : `${courtCount} courts`}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 3 }}>
                              {merged.map((g, i) => (
                                <div key={i} style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.3, display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
                                  {courtCount > 1 && <span style={{ fontWeight: 500 }}>{g.court}</span>}
                                  <span className="sb-mono" style={{ color: 'var(--ink-3)' }}>
                                    {g.ranges.map((r, j) => (
                                      <span key={j}>{j > 0 && <span style={{ color: 'var(--ink-4)' }}>, </span>}{r.start}–{r.end}</span>
                                    ))}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="sb-bk-court">{isRental ? 'Rental' : b0.ground_id}</div>
                        )}
                        <div className="sb-bk-facility">
                          {locationName} · #{firstBid.slice(-6)}
                        </div>
                        {b0Status === 'cancelled' && (
                          <span className="sb-badge bad" style={{ marginTop: 4, display: 'inline-flex' }}>Cancelled</span>
                        )}
                      </div>

                      {/* Tags & notes */}
                      <div className="sb-bk-tags">
                        {isRental && <span className="sb-badge info"><ShoppingBag size={10} /> Rental</span>}
                        {isMultiTime && (
                          <span className="sb-badge plain" style={{ color: 'var(--info-fg)', borderColor: 'var(--info-bg)' }}>
                            multi-time
                          </span>
                        )}
                        {isGroup && hasGroupRentals && (
                          <span className="sb-badge plain">
                            <ShoppingBag size={10} /> {totalRentalQty} item{totalRentalQty !== 1 ? 's' : ''} · ₹{rentalTotal}
                          </span>
                        )}
                        {!isGroup && b0.rentals?.length > 0 && (
                          <span className="sb-badge plain">
                            <ShoppingBag size={10} /> {b0.rentals.length} rental · ₹{b0.rentals.reduce((s, r) => s + r.total, 0)}
                          </span>
                        )}
                        {b0.notes && (
                          <span className="sb-badge plain" title={b0.notes} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            ✱ {b0.notes}
                          </span>
                        )}
                      </div>

                      {/* Money */}
                      <div className="sb-bk-money">
                        {totalAmt > 0 ? (
                          <>
                            <div><span style={{ color: 'var(--ink-3)' }}>₹</span>{totalAmt.toLocaleString('en-IN')}</div>
                            {dueAmt > 0 && b0Status !== 'cancelled' && (
                              <div className="sb-bk-due">₹{dueAmt.toLocaleString('en-IN')} due</div>
                            )}
                            {dueAmt === 0 && collectedAmt > 0 && b0Status !== 'cancelled' && (
                              <div className="sb-bk-paid">paid</div>
                            )}
                          </>
                        ) : collectedAmt > 0 ? (
                          <>
                            <div><span style={{ color: 'var(--ink-3)' }}>₹</span>{collectedAmt.toLocaleString('en-IN')}</div>
                            <div className="sb-bk-paid">paid</div>
                          </>
                        ) : (
                          <div style={{ color: 'var(--ink-4)' }}>—</div>
                        )}
                      </div>

                      {/* Actions */}
                      {/* Row actions removed — Edit / Record / Cancel live in the booking drawer (click the row). */}
                      <div className="sb-bk-acts" />
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {!initialLoad && pagination.total > 0 && (
        <div className="sb-pagination">
          <div className="sb-pag-info">
            {pagination.total_groups} session{pagination.total_groups !== 1 ? 's' : ''} · {pagination.total} booking{pagination.total !== 1 ? 's' : ''}
          </div>
          <div className="sb-pag-pages">
            <button
              className="sb-pag-pill"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || fetching}
            >
              ‹
            </button>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.pages || Math.abs(p - page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…' ? (
                  <span key={`e-${i}`} style={{ padding: '0 4px', color: 'var(--ink-4)', fontSize: 12 }}>…</span>
                ) : (
                  <button
                    key={p}
                    className={`sb-pag-pill ${p === page ? 'active' : ''}`}
                    onClick={() => setPage(p)}
                    disabled={fetching}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              className="sb-pag-pill"
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page >= pagination.pages || fetching}
            >
              ›
            </button>
          </div>
          <div className="sb-pag-size">
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={e => { setPage(1); setPageSize(Number(e.target.value)); }}
            >
              {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      )}

      {paymentBooking && (
        <PaymentDialog
          booking={paymentBooking}
          slotTotal={calcSlotTotal(paymentBooking)}
          api={api}
          onClose={() => setPaymentBooking(null)}
          onSave={fetchBookings}
        />
      )}
      {groupPaymentGroup && (
        <GroupPaymentDialog
          group={groupPaymentGroup}
          calcSlotTotal={calcSlotTotal}
          api={api}
          onClose={() => setGroupPaymentGroup(null)}
          onSave={fetchBookings}
        />
      )}

      {drawerBooking && (
        <BookingDrawer
          booking={drawerBooking}
          group={drawerGroup}
          facilityName={locationOptions.find(l => l.id === drawerBooking.location_id)?.name}
          slotTotal={drawerTotal || calcSlotTotal(drawerBooking)}
          api={api}
          onClose={() => setDrawerBooking(null)}
          onMutated={() => { fetchBookings(); setDrawerBooking(null); }}
          onEdit={(b) => navigate(`/admin/bookings/${b.booking_id}/edit`, { state: { booking: b, group: drawerGroup || [b] } })}
        />
      )}

      {cancelTarget && (
        <CancelDialog
          group={cancelTarget.group}
          cancelling={cancelling}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
}
